import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import {
  useRoutine,
  useCreateRoutine,
  useUpdateRoutine,
  useDeleteRoutine,
  useAddRoutineExercise,
  useUpdateRoutineExercise,
  useRemoveRoutineExercise,
  useExercises,
  type RoutineExercise,
} from "@/hooks/useApi";

type ExConfig = {
  sets: string;
  min_reps: string;
  max_reps: string;
  weight_step_kg: string;
  starting_weight_kg: string;
};

const DEFAULT_CONFIG: ExConfig = {
  sets: "3",
  min_reps: "8",
  max_reps: "12",
  weight_step_kg: "2.5",
  starting_weight_kg: "0",
};

function configToNumbers(c: ExConfig) {
  return {
    sets: parseInt(c.sets) || 3,
    min_reps: parseInt(c.min_reps) || 8,
    max_reps: parseInt(c.max_reps) || 12,
    weight_step_kg: parseFloat(c.weight_step_kg) || 2.5,
    starting_weight_kg: parseFloat(c.starting_weight_kg) || 0,
  };
}

function ExConfigForm({
  value,
  onChange,
}: {
  value: ExConfig;
  onChange: (v: ExConfig) => void;
}) {
  const field = (key: keyof ExConfig, label: string, placeholder: string) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-zinc-500">{label}</label>
      <input
        type="number"
        inputMode="decimal"
        value={value[key]}
        onChange={(e) => onChange({ ...value, [key]: e.target.value })}
        placeholder={placeholder}
        className="w-full rounded border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-center text-zinc-50 focus:outline-none focus:ring-1 focus:ring-zinc-600"
      />
    </div>
  );

  return (
    <div className="grid grid-cols-3 gap-2 mt-2">
      {field("sets", "Sets", "3")}
      {field("min_reps", "Min reps", "8")}
      {field("max_reps", "Max reps", "12")}
      {field("starting_weight_kg", "Weight (kg)", "0")}
      {field("weight_step_kg", "Step (kg)", "2.5")}
    </div>
  );
}

export default function RoutineEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const { data: routine } = useRoutine(id ?? "");
  const { data: exercises = [] } = useExercises();

  const [name, setName] = useState(routine?.name ?? "");
  const [description, setDescription] = useState(routine?.description ?? "");

  useEffect(() => {
    if (routine) {
      setName(routine.name);
      setDescription(routine.description ?? "");
    }
  }, [routine]);

  const createMutation = useCreateRoutine();
  const updateMutation = useUpdateRoutine(id ?? "");
  const deleteMutation = useDeleteRoutine();
  const addExMutation = useAddRoutineExercise(id ?? "");
  const updateExMutation = useUpdateRoutineExercise();
  const removeExMutation = useRemoveRoutineExercise(id ?? "");

  const [showExPicker, setShowExPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [saveFeedback, setSaveFeedback] = useState<{ type: "idle" | "saving" | "saved" | "error"; message?: string }>({ type: "idle" });
  // Two-step add: after picking exercise, configure before adding
  const [pendingExId, setPendingExId] = useState<string | null>(null);
  const [pendingConfig, setPendingConfig] = useState<ExConfig>(DEFAULT_CONFIG);
  // Inline edit state per exercise id
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editConfig, setEditConfig] = useState<ExConfig>(DEFAULT_CONFIG);

  async function save() {
    if (!name.trim()) return;
    setSaveFeedback({ type: "saving" });

    try {
      if (isNew) {
        const r = await createMutation.mutateAsync({ name, description });
        navigate(`/routines/${r.id}`);
      } else {
        await updateMutation.mutateAsync({ name, description });
        setSaveFeedback({ type: "saved", message: "Saved" });
        window.setTimeout(() => setSaveFeedback({ type: "idle" }), 1500);
      }
    } catch (error) {
      setSaveFeedback({ type: "error", message: error instanceof Error ? error.message : "Unable to save routine" });
    }
  }

  function openEdit(ex: RoutineExercise) {
    setEditingId(ex.id);
    setEditConfig({
      sets: String(ex.sets),
      min_reps: String(ex.min_reps),
      max_reps: String(ex.max_reps),
      weight_step_kg: String(ex.weight_step_kg),
      starting_weight_kg: String(ex.starting_weight_kg ?? 0),
    });
  }

  async function saveEdit(exId: string) {
    await updateExMutation.mutateAsync({ routineId: id ?? "", exId, data: configToNumbers(editConfig) });
    setEditingId(null);
  }

  async function confirmAdd() {
    if (!pendingExId) return;
    await addExMutation.mutateAsync({
      exercise_id: pendingExId,
      ...configToNumbers(pendingConfig),
      display_order: routine?.exercises.length ?? 0,
    });
    setPendingExId(null);
    setPendingConfig(DEFAULT_CONFIG);
  }

  const filtered = exercises.filter((e) =>
    e.name.toLowerCase().includes(pickerSearch.toLowerCase())
  );
  const grouped = filtered.reduce<Record<string, typeof filtered>>((acc, ex) => {
    (acc[ex.muscle_group] ??= []).push(ex);
    return acc;
  }, {});

  return (
    <div className="p-4 space-y-5">
      <h1 className="text-xl font-semibold">{isNew ? "New Routine" : "Edit Routine"}</h1>

      <div className="space-y-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Routine name"
          className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-zinc-600"
        />
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-zinc-600"
        />
        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={!name.trim() || createMutation.isPending || updateMutation.isPending}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-zinc-950 disabled:opacity-40 hover:bg-brand/80 transition-colors"
          >
            {createMutation.isPending || updateMutation.isPending ? "Saving..." : isNew ? "Create" : "Save"}
          </button>
          {!isNew && (
            <button
              onClick={() => deleteMutation.mutate(id!, { onSuccess: () => navigate("/routines") })}
              className="rounded-lg border border-zinc-800 px-4 py-2 text-sm text-red-500 hover:border-red-900 hover:bg-red-950/30 transition-colors"
            >
              Delete routine
            </button>
          )}
        </div>
        <p className={`text-sm ${saveFeedback.type === "saved" ? "text-green-400" : saveFeedback.type === "error" ? "text-red-400" : "text-zinc-500"}`}>
          {saveFeedback.type === "saving" ? "Saving..." : saveFeedback.type === "saved" ? saveFeedback.message : saveFeedback.type === "error" ? saveFeedback.message : ""}
        </p>
      </div>

      {!isNew && routine && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">Exercises</h2>
            <button
              onClick={() => setShowExPicker(true)}
              className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-200"
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>

          {routine.exercises.length === 0 ? (
            <p className="text-sm text-zinc-500">No exercises yet.</p>
          ) : (
            <ul className="space-y-2">
              {routine.exercises.map((ex) => {
                const isEditing = editingId === ex.id;
                return (
                  <li key={ex.id} className="rounded-lg border border-zinc-800">
                    <div className="flex items-center justify-between p-3">
                      <button className="flex-1 text-left" onClick={() => isEditing ? setEditingId(null) : openEdit(ex)}>
                        <p className="text-sm font-medium">{ex.exercise_name}</p>
                        <p className="text-xs text-zinc-500">
                          {ex.sets} × {ex.min_reps}–{ex.max_reps} reps · {ex.starting_weight_kg ?? 0}kg · +{ex.weight_step_kg}kg step
                        </p>
                      </button>
                      <div className="flex items-center gap-2">
                        {isEditing ? (
                          <ChevronUp className="h-4 w-4 text-zinc-600" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-zinc-600" />
                        )}
                        <button
                          onClick={() => removeExMutation.mutate(ex.id)}
                          className="text-zinc-600 hover:text-red-400 transition-colors ml-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {isEditing && (
                      <div className="border-t border-zinc-800 p-3 space-y-3">
                        <ExConfigForm value={editConfig} onChange={setEditConfig} />
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveEdit(ex.id)}
                            className="rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-zinc-950 hover:bg-brand/80 transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="rounded-lg border border-zinc-800 px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Exercise picker */}
      {showExPicker && !pendingExId && (
        <ExercisePicker
          grouped={grouped}
          pickerSearch={pickerSearch}
          setPickerSearch={setPickerSearch}
          onSelect={(exId) => {
            setPendingExId(exId);
            setShowExPicker(false);
            setPickerSearch("");
          }}
          onClose={() => { setShowExPicker(false); setPickerSearch(""); }}
        />
      )}

      {/* Config form after picking exercise */}
      {pendingExId && (
        <div className="fixed inset-0 z-50 flex flex-col bg-zinc-950">
          <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
            <h2 className="font-semibold">Configure Exercise</h2>
            <button
              onClick={() => { setPendingExId(null); setShowExPicker(true); }}
              className="text-zinc-500 hover:text-zinc-200 text-sm"
            >
              ← Back
            </button>
          </div>
          <div className="flex-1 p-4 space-y-4">
            <p className="text-sm text-zinc-400">
              {exercises.find((e) => e.id === pendingExId)?.name}
            </p>
            <ExConfigForm value={pendingConfig} onChange={setPendingConfig} />
          </div>
          <div className="p-4 border-t border-zinc-800">
            <button
              onClick={confirmAdd}
              disabled={addExMutation.isPending}
              className="w-full rounded-lg bg-brand py-3 text-sm font-semibold text-zinc-950 disabled:opacity-40 hover:bg-brand/80 transition-colors"
            >
              Add to Routine
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ExercisePicker({
  grouped,
  pickerSearch,
  setPickerSearch,
  onSelect,
  onClose,
}: {
  grouped: Record<string, Array<{ id: string; name: string; muscle_group: string }>>;
  pickerSearch: string;
  setPickerSearch: (s: string) => void;
  onSelect: (id: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-zinc-950">
      <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
        <h2 className="font-semibold">Pick Exercise</h2>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200 text-sm">
          Cancel
        </button>
      </div>
      <div className="p-3">
        <input
          autoFocus
          value={pickerSearch}
          onChange={(e) => setPickerSearch(e.target.value)}
          placeholder="Search exercises…"
          className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-zinc-600"
        />
      </div>
      <div className="flex-1 overflow-y-auto">
        {Object.entries(grouped).map(([group, exs]) => (
          <div key={group}>
            <p className="px-4 py-1 text-xs font-medium uppercase tracking-wider text-zinc-500 bg-zinc-900">
              {group}
            </p>
            {exs.map((ex) => (
              <button
                key={ex.id}
                onClick={() => onSelect(ex.id)}
                className="w-full px-4 py-3 text-left text-sm hover:bg-zinc-800 transition-colors"
              >
                {ex.name}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
